import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";

import { showQuiz, updateQuiz, saveSteps, uploadQuizImage } from "../../services/quizService";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

// Garante URL absoluta para arquivos do backend (caminhos /public/... precisam do host do backend)
function resolveUrl(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${getBackendUrl()}${url}`;
}

const BLOCK_TYPES = [
  { group: "Captura de Lead", items: [
    { type: "lead_name", label: "Nome", icon: "👤", desc: "Campo de nome completo" },
    { type: "lead_email", label: "E-mail", icon: "✉️", desc: "Campo de e-mail" },
    { type: "lead_phone", label: "Telefone / WhatsApp", icon: "📱", desc: "Campo de telefone" },
  ]},
  { group: "Perguntas", items: [
    { type: "text_short", label: "Texto curto", icon: "💬", desc: "Resposta em texto" },
    { type: "text_long", label: "Texto longo", icon: "📝", desc: "Resposta longa" },
    { type: "single_choice", label: "Escolha única", icon: "🔘", desc: "Selecionar uma opção" },
    { type: "multiple_choice", label: "Múltipla escolha", icon: "☑️", desc: "Selecionar várias" },
    { type: "yes_no", label: "Sim / Não", icon: "✅", desc: "Resposta binária" },
    { type: "scale", label: "Escala 1-10", icon: "📊", desc: "Escala numérica" },
    { type: "nps", label: "NPS (0-10)", icon: "⭐", desc: "Net Promoter Score" },
    { type: "number", label: "Número", icon: "🔢", desc: "Entrada numérica" },
    { type: "date", label: "Data", icon: "📅", desc: "Seletor de data" },
  ]},
  { group: "Conteúdo", items: [
    { type: "statement", label: "Declaração", icon: "📢", desc: "Texto informativo sem resposta" },
    { type: "video", label: "Vídeo", icon: "🎬", desc: "Embed de YouTube ou Vimeo" },
    { type: "image", label: "Imagem", icon: "🖼️", desc: "Exibir uma imagem" },
    { type: "wait", label: "Espera", icon: "⏳", desc: "Pausa com contagem regressiva" },
  ]},
  { group: "Integração", items: [
    { type: "payment", label: "Pagamento", icon: "💳", desc: "Link de pagamento integrado" },
    { type: "schedule", label: "Agendamento", icon: "📅", desc: "Criar compromisso na agenda" },
    { type: "api_request", label: "API Externa", icon: "🔌", desc: "Chamar API externa com dados do formulário" },
  ]},
  { group: "Final", items: [
    { type: "result", label: "Resultado", icon: "🎉", desc: "Mensagem de conclusão" },
    { type: "redirect", label: "Redirecionamento", icon: "🔗", desc: "Redirecionar para URL" },
  ]},
];

const TYPE_META = {};
BLOCK_TYPES.forEach(g => g.items.forEach(i => { TYPE_META[i.type] = i; }));

const NON_ANSWER_TYPES = ["statement", "result", "redirect", "video", "image", "wait", "api_request"];

function getInitialConfig(type) {
  if (type === "scale") return { min: 1, max: 10 };
  if (type === "nps") return { min: 0, max: 10 };
  if (type === "video") return { videoUrl: "" };
  if (type === "image") return { imageUrl: "" };
  if (type === "payment") return { productName: "", amount: 0, description: "" };
  if (type === "schedule") return {
    serviceName: "", durationMinutes: 60, allowFutureDays: 30,
    timeSlots: ["08:00", "09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"],
  };
  if (type === "wait") return { waitSeconds: 5, showCountdown: true, autoAdvance: true };
  if (type === "api_request") return {
    apiUrl: "", method: "GET",
    headers: [{ key: "Content-Type", value: "application/json" }],
    bodyTemplate: "",
    showResponse: true,
    responseMessage: "Dados enviados com sucesso!",
  };
  return {};
}

function newStep(type) {
  const meta = TYPE_META[type] || {};
  return {
    _tempId: Math.random().toString(36).slice(2),
    type,
    question: meta.label,
    description: "",
    required: !NON_ANSWER_TYPES.includes(type),
    variableName: "",
    placeholder: "",
    options: (type === "single_choice" || type === "multiple_choice")
      ? [{ label: "Opção 1", value: "opcao_1", score: 0 }, { label: "Opção 2", value: "opcao_2", score: 0 }]
      : [],
    logic: null,
    config: getInitialConfig(type),
  };
}

function getVideoEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  if (url.includes("youtube.com/embed/") || url.includes("player.vimeo.com")) return url;
  return null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f5f6fa",
    overflow: "hidden",
  },
  topbar: {
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    height: 56,
    flexShrink: 0,
    gap: 8,
  },
  topbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: "0 0 auto",
    maxWidth: 280,
  },
  topbarTitle: {
    fontWeight: 700,
    fontSize: 15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#1e293b",
  },
  topbarCenter: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  topbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: "0 0 auto",
  },
  topbarTabs: {
    "& .MuiTab-root": { minWidth: 90, fontSize: 13, padding: "6px 12px" },
  },
  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  // Coluna esquerda: lista de etapas
  stepsPanel: {
    width: 220,
    flexShrink: 0,
    background: "#1e1e2e",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  stepsHeader: {
    padding: "12px 14px 8px",
    color: "#9ca3af",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  stepItem: {
    padding: "10px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderLeft: "3px solid transparent",
    transition: "all 0.15s",
    "&:hover": { background: "rgba(255,255,255,0.06)" },
  },
  stepItemActive: {
    background: "rgba(108,99,255,0.15)",
    borderLeftColor: "#6C63FF",
  },
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#374151",
    color: "#d1d5db",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  stepNumActive: {
    background: "#6C63FF",
    color: "#fff",
  },
  stepLabel: {
    fontSize: 13,
    color: "#d1d5db",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  // Coluna meio: paleta de blocos
  blocksPanel: {
    width: 200,
    flexShrink: 0,
    background: "#fff",
    borderRight: "1px solid #e5e7eb",
    overflowY: "auto",
    padding: "12px 0",
  },
  blockGroup: {
    padding: "4px 0",
  },
  blockGroupLabel: {
    padding: "6px 14px 4px",
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  blockItem: {
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "background 0.1s",
    "&:hover": { background: "#f3f4f6" },
  },
  blockIcon: {
    fontSize: 18,
    width: 24,
    textAlign: "center",
  },
  blockLabel: {
    fontSize: 13,
    color: "#374151",
  },
  // Preview do celular
  previewPanel: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f2f5",
    overflow: "auto",
    padding: 24,
  },
  phone: {
    width: 360,
    height: 640,
    borderRadius: 32,
    border: "8px solid #1e1e2e",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  phoneScreen: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  // Painel de propriedades
  propsPanel: {
    width: 280,
    flexShrink: 0,
    background: "#fff",
    borderLeft: "1px solid #e5e7eb",
    overflowY: "auto",
    padding: "16px",
  },
  propSection: {
    marginBottom: 20,
  },
  propLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
  },
}));

// ---------- Renderizador de mídia da etapa (builder e público) ----------
function StepMedia({ config, maxHeight = 160 }) {
  const media = config?.media;
  if (!media || !media.type || media.type === "none") return null;

  if (media.type === "icon") {
    return (
      <div style={{ textAlign: "center", fontSize: 52, lineHeight: 1, marginBottom: 4 }}>
        {media.icon || "❓"}
      </div>
    );
  }
  if (media.type === "image") {
    const src = resolveUrl(media.url);
    return src
      ? <img src={src} alt="" style={{ width: "100%", borderRadius: 10, objectFit: "cover", maxHeight, display: "block" }} />
      : <div style={{ background: "#f3f4f6", borderRadius: 8, height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>🖼️ Imagem / GIF não configurado</div>;
  }
  if (media.type === "video") {
    const embedUrl = getVideoEmbedUrl(media.url || "");
    return embedUrl
      ? <iframe src={embedUrl} title="step-media" style={{ width: "100%", height: maxHeight, borderRadius: 8, border: "none", display: "block" }} />
      : <div style={{ background: "#1e1e2e", borderRadius: 8, height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>🎬 URL do vídeo não configurada</div>;
  }
  return null;
}

// ---------- Preview de uma etapa ----------
function StepPreview({ step, quiz }) {
  const bg = quiz?.bgColor || "#fff";
  const primary = quiz?.primaryColor || "#6C63FF";
  const btnText = quiz?.buttonText || "Continuar";

  const inputStyle = {
    width: "100%",
    padding: "12px",
    border: `1.5px solid ${primary}44`,
    borderRadius: 8,
    fontSize: 15,
    outline: "none",
    fontFamily: "inherit",
    background: "rgba(255,255,255,0.9)",
    boxSizing: "border-box",
  };

  const btnStyle = {
    width: "100%",
    padding: "14px",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 12,
  };

  const renderInput = () => {
    if (!step) return null;
    switch (step.type) {
      case "lead_name":
      case "lead_email":
      case "lead_phone":
      case "text_short":
        return <input style={inputStyle} placeholder={step.placeholder || step.question} readOnly />;
      case "text_long":
        return <textarea style={{ ...inputStyle, height: 80, resize: "none" }} placeholder={step.placeholder || step.question} readOnly />;
      case "number":
        return <input type="number" style={inputStyle} placeholder={step.placeholder || "0"} readOnly />;
      case "date":
        return <input type="date" style={inputStyle} readOnly />;
      case "single_choice":
        return (
          <Box display="flex" flexDirection="column" gap={1}>
            {(step.options || []).map((o, i) => (
              <Box key={i} style={{
                padding: "10px 14px", border: `1.5px solid ${primary}44`, borderRadius: 8,
                cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 8
              }}>
                <Box style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${primary}`, flexShrink: 0 }} />
                {o.label}
              </Box>
            ))}
          </Box>
        );
      case "multiple_choice":
        return (
          <Box display="flex" flexDirection="column" gap={1}>
            {(step.options || []).map((o, i) => (
              <Box key={i} style={{
                padding: "10px 14px", border: `1.5px solid ${primary}44`, borderRadius: 8,
                cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 8
              }}>
                <Box style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${primary}`, flexShrink: 0 }} />
                {o.label}
              </Box>
            ))}
          </Box>
        );
      case "yes_no":
        return (
          <Box display="flex" gap={1}>
            {["Sim", "Não"].map(l => (
              <Box key={l} flex={1} style={{
                padding: "12px 8px", border: `1.5px solid ${primary}44`, borderRadius: 8,
                textAlign: "center", cursor: "pointer", fontWeight: 600, fontSize: 15
              }}>{l}</Box>
            ))}
          </Box>
        );
      case "scale":
      case "nps": {
        const min = step.config?.min ?? (step.type === "nps" ? 0 : 1);
        const max = step.config?.max ?? 10;
        return (
          <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
            {Array.from({ length: max - min + 1 }, (_, i) => i + min).map(n => (
              <Box key={n} style={{
                width: 40, height: 40, borderRadius: 8, border: `1.5px solid ${primary}44`,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", fontWeight: 600, fontSize: 15
              }}>{n}</Box>
            ))}
          </Box>
        );
      }
      case "statement":
        return null;
      case "video": {
        const embedUrl = getVideoEmbedUrl(step.config?.videoUrl || "");
        return embedUrl
          ? <iframe src={embedUrl} title="video" style={{ width: "100%", height: 150, borderRadius: 8, border: "none" }} />
          : <Box style={{ background: "#1e1e2e", borderRadius: 8, height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>🎬 Cole a URL do YouTube ou Vimeo</Box>;
      }
      case "image": {
        const imgUrl = resolveUrl(step.config?.imageUrl || "");
        return imgUrl
          ? <img src={imgUrl} alt={step.question || "imagem"} style={{ width: "100%", borderRadius: 8, objectFit: "cover", maxHeight: 130 }} />
          : <Box style={{ background: "#f3f4f6", borderRadius: 8, height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>🖼️ Faça upload de uma imagem</Box>;
      }
      case "payment": {
        const amount = step.config?.amount || 0;
        const productName = step.config?.productName || "Produto";
        return (
          <Box style={{ border: `1.5px solid ${primary}33`, borderRadius: 10, padding: 12 }}>
            <Typography style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>💳 {productName}</Typography>
            <Typography style={{ fontSize: 20, fontWeight: 800, color: primary, marginBottom: 8 }}>
              R$ {Number(amount).toFixed(2).replace(".", ",")}
            </Typography>
            {step.config?.description && <Typography style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{step.config.description}</Typography>}
            <button style={btnStyle}>Ir para pagamento →</button>
          </Box>
        );
      }
      case "schedule": {
        const slots = (step.config?.timeSlots || ["08:00","09:00","10:00","11:00"]).slice(0, 4);
        return (
          <Box>
            <input type="date" style={inputStyle} readOnly />
            <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
              {slots.map(t => (
                <Box key={t} style={{ padding: "6px 10px", border: `1.5px solid ${primary}33`, borderRadius: 6, fontSize: 12, color: "#374151" }}>{t}</Box>
              ))}
            </Box>
          </Box>
        );
      }
      case "wait":
        return (
          <Box textAlign="center" py={2}>
            <Box style={{ fontSize: 40, marginBottom: 8 }}>⏳</Box>
            <Typography style={{ color: primary, fontWeight: 800, fontSize: 32 }}>
              {step.config?.waitSeconds || 5}
            </Typography>
            <Typography style={{ fontSize: 13, color: "#6b7280" }}>segundos de espera</Typography>
          </Box>
        );
      case "api_request":
        return (
          <Box style={{ border: `1.5px solid ${primary}33`, borderRadius: 10, padding: 12 }}>
            <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>🔌 API Externa</Typography>
            <Typography style={{ fontSize: 11, color: "#6b7280", fontFamily: "monospace", wordBreak: "break-all" }}>
              {step.config?.method || "GET"} {step.config?.apiUrl || "https://..."}
            </Typography>
            {step.config?.responseMessage && (
              <Typography style={{ fontSize: 11, color: "#16a34a", marginTop: 6 }}>
                ✅ {step.config.responseMessage}
              </Typography>
            )}
          </Box>
        );
      case "result":
        return (
          <Box textAlign="center" py={2}>
            <Box style={{ fontSize: 48, marginBottom: 8 }}>🎉</Box>
            <Typography style={{ color: primary, fontWeight: 700, fontSize: 18 }}>
              {step.config?.resultTitle || "Obrigado!"}
            </Typography>
          </Box>
        );
      case "redirect":
        return (
          <Box style={{
            padding: 12, background: "#f3f4f6", borderRadius: 8, fontSize: 13, color: "#6b7280"
          }}>
            🔗 {step.config?.redirectUrl || "URL de redirecionamento"}
          </Box>
        );
      default:
        return null;
    }
  };

  if (!step) return (
    <Box display="flex" flex={1} alignItems="center" justifyContent="center" style={{ color: "#9ca3af", fontSize: 14 }}>
      Selecione uma etapa
    </Box>
  );

  return (
    <Box style={{ background: bg, flex: 1, padding: "24px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
      <StepMedia config={step.config} maxHeight={150} />
      {step.type !== "result" && step.type !== "redirect" && (
        <>
          {step.question && (
            <Typography style={{ fontWeight: 700, fontSize: 18, color: "#1a1a2e", lineHeight: 1.4 }}>
              {step.question}
            </Typography>
          )}
          {step.description && (
            <Typography style={{ fontSize: 14, color: "#6b7280" }}>{step.description}</Typography>
          )}
        </>
      )}
      {renderInput()}
      {!["result", "redirect", "payment"].includes(step.type) && (
        <button style={btnStyle}>{btnText}</button>
      )}
    </Box>
  );
}

// ---------- Painel de propriedades de uma etapa ----------
const QUICK_ICONS = ["🎯","🚀","💡","❓","✅","🔍","💬","📊","🎉","⭐","🏆","💰","📅","📞","✉️","👤","🏠","🔐","💳","🎓","🤝","🔥","⚡","🎁","🛒","🎵","📸","🌟","✨","💪"];

function PropsPanel({ step, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, allSteps }) {
  const classes = useStyles();
  const imageInputRef = useRef(null);
  const mediaImageInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingMediaImage, setUploadingMediaImage] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const stepKey = step?._tempId || step?.id;
  useEffect(() => {
    setMediaOpen(!!(step?.config?.media?.type && step.config.media.type !== "none"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadQuizImage(file);
      onChange("config", { ...step.config, imageUrl: url });
    } catch {
      toast.error("Erro ao fazer upload da imagem");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleMediaImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMediaImage(true);
    try {
      const url = await uploadQuizImage(file);
      onChange("config", { ...step.config, media: { ...(step.config?.media || {}), type: "image", url } });
    } catch {
      toast.error("Erro ao fazer upload da mídia");
    } finally {
      setUploadingMediaImage(false);
      e.target.value = "";
    }
  };

  const updateMedia = (fields) => {
    onChange("config", { ...step.config, media: { ...(step.config?.media || {}), ...fields } });
  };

  if (!step) return (
    <Box className={classes.propsPanel} display="flex" alignItems="center" justifyContent="center" style={{ color: "#9ca3af", textAlign: "center" }}>
      <Box>
        <Box style={{ fontSize: 40, marginBottom: 8 }}>👆</Box>
        <Typography variant="body2">Selecione uma etapa para editar suas propriedades</Typography>
      </Box>
    </Box>
  );

  const meta = TYPE_META[step.type] || {};

  const addOption = () => {
    const opts = [...(step.options || [])];
    const idx = opts.length + 1;
    opts.push({ label: `Opção ${idx}`, value: `opcao_${idx}`, score: 0 });
    onChange("options", opts);
  };

  const updateOption = (i, field, val) => {
    const opts = [...(step.options || [])];
    opts[i] = { ...opts[i], [field]: val };
    // auto-slug value from label
    if (field === "label") opts[i].value = val.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "_").substring(0, 30);
    onChange("options", opts);
  };

  const removeOption = (i) => {
    const opts = [...(step.options || [])];
    opts.splice(i, 1);
    onChange("options", opts);
  };

  // Lógica: conditional next steps
  const updateLogic = (conditions, defaultNextStepId) => {
    onChange("logic", { conditions, defaultNextStepId });
  };

  const logic = step.logic || { conditions: [], defaultNextStepId: null };

  return (
    <Box className={classes.propsPanel}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography style={{ fontWeight: 700, fontSize: 14 }}>
          {meta.icon} {meta.label}
        </Typography>
        <Box>
          <Tooltip title="Mover para cima">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={isFirst}><ArrowUpwardIcon fontSize="small" /></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mover para baixo">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={isLast}><ArrowDownwardIcon fontSize="small" /></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Excluir etapa">
            <IconButton size="small" onClick={onDelete} style={{ color: "#ef4444" }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ===== Seção Mídia — disponível em todas as etapas ===== */}
      <Box className={classes.propSection} style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 12px", marginBottom: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ cursor: "pointer" }}
          onClick={() => setMediaOpen(v => !v)}
        >
          <Typography style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            🎨 Mídia da etapa
          </Typography>
          <Typography style={{ fontSize: 11, color: "#6C63FF" }}>{mediaOpen ? "▲" : "▼"}</Typography>
        </Box>

        {mediaOpen && (
          <Box mt={1.5}>
            <Select
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.media?.type || "none"}
              onChange={(e) => {
                const type = e.target.value;
                if (type === "none") updateMedia({ type: "none", url: "", icon: "" });
                else updateMedia({ type });
              }}
            >
              <MenuItem value="none">Sem mídia</MenuItem>
              <MenuItem value="icon">Ícone / Emoji</MenuItem>
              <MenuItem value="image">Imagem / GIF</MenuItem>
              <MenuItem value="video">Vídeo (YouTube / Vimeo)</MenuItem>
            </Select>

            {/* Config ícone */}
            {step.config?.media?.type === "icon" && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={step.config?.media?.icon || ""}
                  onChange={(e) => updateMedia({ icon: e.target.value })}
                  placeholder="Digite ou cole um emoji 🚀"
                  inputProps={{ style: { fontSize: 20, textAlign: "center" } }}
                />
                <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 8 }}>
                  {QUICK_ICONS.map(em => (
                    <Box
                      key={em}
                      onClick={() => updateMedia({ icon: em })}
                      style={{
                        fontSize: 22,
                        cursor: "pointer",
                        padding: "4px 5px",
                        borderRadius: 6,
                        background: step.config?.media?.icon === em ? "#6C63FF22" : "transparent",
                        border: step.config?.media?.icon === em ? "1.5px solid #6C63FF" : "1.5px solid transparent",
                        transition: "all 0.1s",
                      }}
                    >
                      {em}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Config imagem / GIF */}
            {step.config?.media?.type === "image" && (
              <Box mt={1}>
                {step.config?.media?.url && (
                  <img
                    src={resolveUrl(step.config.media.url)}
                    alt="preview mídia"
                    style={{ width: "100%", borderRadius: 8, marginBottom: 8, objectFit: "cover", maxHeight: 100 }}
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={mediaImageInputRef}
                  onChange={handleMediaImageUpload}
                />
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={uploadingMediaImage}
                  onClick={() => mediaImageInputRef.current?.click()}
                >
                  {uploadingMediaImage ? <CircularProgress size={16} /> : (step.config?.media?.url ? "Alterar" : "Upload de imagem / GIF")}
                </Button>
                {step.config?.media?.url && (
                  <Button
                    size="small"
                    color="secondary"
                    style={{ marginTop: 6 }}
                    onClick={() => updateMedia({ url: "" })}
                  >
                    Remover
                  </Button>
                )}
              </Box>
            )}

            {/* Config vídeo */}
            {step.config?.media?.type === "video" && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={step.config?.media?.url || ""}
                  onChange={(e) => updateMedia({ url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Pergunta / mensagem */}
      {!["redirect", "image"].includes(step.type) && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>
            {["statement", "result", "video"].includes(step.type) ? "Legenda (opcional)" : "Pergunta"}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            size="small"
            value={step.question || ""}
            onChange={(e) => onChange("question", e.target.value)}
          />
        </Box>
      )}

      {/* Descrição */}
      {!["redirect", "result", "video", "image", "payment", "schedule"].includes(step.type) && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Descrição (opcional)</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.description || ""}
            onChange={(e) => onChange("description", e.target.value)}
          />
        </Box>
      )}

      {/* Placeholder */}
      {["lead_name","lead_email","lead_phone","text_short","text_long","number"].includes(step.type) && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Placeholder</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.placeholder || ""}
            onChange={(e) => onChange("placeholder", e.target.value)}
          />
        </Box>
      )}

      {/* Nome da variável */}
      {!["statement","result","redirect","video","image","payment","schedule"].includes(step.type) && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Nome da variável</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.variableName || ""}
            onChange={(e) => onChange("variableName", e.target.value)}
            placeholder="ex: nome_cliente"
          />
        </Box>
      )}

      {/* Obrigatório */}
      {!["statement","result","redirect","video","image"].includes(step.type) && (
        <Box className={classes.propSection}>
          <FormControlLabel
            control={
              <Switch
                checked={!!step.required}
                onChange={(e) => onChange("required", e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 13 }}>Obrigatório</Typography>}
          />
        </Box>
      )}

      {/* Opções para single/multiple choice */}
      {(step.type === "single_choice" || step.type === "multiple_choice") && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Opções</Typography>
          {(step.options || []).map((opt, i) => (
            <Box key={i} className={classes.optionRow}>
              <TextField
                size="small"
                variant="outlined"
                value={opt.label}
                onChange={(e) => updateOption(i, "label", e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Pts"
                type="number"
                value={opt.score || 0}
                onChange={(e) => updateOption(i, "score", Number(e.target.value))}
                style={{ width: 56 }}
              />
              <IconButton size="small" onClick={() => removeOption(i)} style={{ color: "#ef4444" }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addOption} style={{ marginTop: 4 }}>
            Adicionar opção
          </Button>
        </Box>
      )}

      {/* Config escala */}
      {(step.type === "scale" || step.type === "nps") && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Intervalo</Typography>
          <Box display="flex" gap={1}>
            <TextField
              label="Mín"
              type="number"
              size="small"
              variant="outlined"
              value={step.config?.min ?? (step.type === "nps" ? 0 : 1)}
              onChange={(e) => onChange("config", { ...step.config, min: Number(e.target.value) })}
            />
            <TextField
              label="Máx"
              type="number"
              size="small"
              variant="outlined"
              value={step.config?.max ?? 10}
              onChange={(e) => onChange("config", { ...step.config, max: Number(e.target.value) })}
            />
          </Box>
        </Box>
      )}

      {/* Config resultado */}
      {step.type === "result" && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Título do resultado</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.config?.resultTitle || ""}
            onChange={(e) => onChange("config", { ...step.config, resultTitle: e.target.value })}
          />
        </Box>
      )}

      {/* Config redirect */}
      {step.type === "redirect" && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>URL de destino</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.config?.redirectUrl || ""}
            onChange={(e) => onChange("config", { ...step.config, redirectUrl: e.target.value })}
            placeholder="https://..."
          />
          <Typography className={classes.propLabel}>Mensagem antes do redirect (opcional)</Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            size="small"
            value={step.question || ""}
            onChange={(e) => onChange("question", e.target.value)}
            placeholder="Você será redirecionado..."
          />
        </Box>
      )}

      {/* Config vídeo */}
      {step.type === "video" && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>URL do vídeo (YouTube ou Vimeo)</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={step.config?.videoUrl || ""}
            onChange={(e) => onChange("config", { ...step.config, videoUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
          />
        </Box>
      )}

      {/* Config imagem */}
      {step.type === "image" && (
        <Box className={classes.propSection}>
          <Typography className={classes.propLabel}>Imagem</Typography>
          {step.config?.imageUrl && (
            <img
              src={resolveUrl(step.config.imageUrl)}
              alt="preview"
              style={{ width: "100%", borderRadius: 8, marginBottom: 8, objectFit: "cover", maxHeight: 110 }}
            />
          )}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={imageInputRef}
            onChange={handleImageUpload}
          />
          <Button
            variant="outlined"
            size="small"
            fullWidth
            disabled={uploadingImage}
            onClick={() => imageInputRef.current?.click()}
          >
            {uploadingImage ? <CircularProgress size={16} /> : (step.config?.imageUrl ? "Alterar imagem" : "Upload de imagem")}
          </Button>
          {step.config?.imageUrl && (
            <Button
              size="small"
              color="secondary"
              style={{ marginTop: 6 }}
              onClick={() => onChange("config", { ...step.config, imageUrl: "" })}
            >
              Remover
            </Button>
          )}
        </Box>
      )}

      {/* Config pagamento */}
      {step.type === "payment" && (
        <>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Nome do produto / serviço</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.productName || ""}
              onChange={(e) => onChange("config", { ...step.config, productName: e.target.value })}
              placeholder="ex: Consultoria 1h"
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Valor (R$)</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={step.config?.amount || ""}
              onChange={(e) => onChange("config", { ...step.config, amount: parseFloat(e.target.value) || 0 })}
              placeholder="0,00"
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Descrição (opcional)</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={step.config?.description || ""}
              onChange={(e) => onChange("config", { ...step.config, description: e.target.value })}
            />
          </Box>
          <Typography style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            O gateway é detectado automaticamente conforme as integrações de pagamento da empresa (Mercado Pago, Asaas ou InfinityPay).
          </Typography>
        </>
      )}

      {/* Config agendamento */}
      {step.type === "schedule" && (
        <>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Nome do serviço</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.serviceName || ""}
              onChange={(e) => onChange("config", { ...step.config, serviceName: e.target.value })}
              placeholder="ex: Consultoria, Avaliação"
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Duração (minutos)</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 5 }}
              value={step.config?.durationMinutes || 60}
              onChange={(e) => onChange("config", { ...step.config, durationMinutes: parseInt(e.target.value) || 60 })}
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Dias disponíveis (a partir de hoje)</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              value={step.config?.allowFutureDays || 30}
              onChange={(e) => onChange("config", { ...step.config, allowFutureDays: parseInt(e.target.value) || 30 })}
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Horários disponíveis</Typography>
            <Typography style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>
              Separados por vírgula (formato HH:MM)
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={(step.config?.timeSlots || []).join(", ")}
              onChange={(e) => {
                const slots = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                onChange("config", { ...step.config, timeSlots: slots });
              }}
              placeholder="08:00, 09:00, 10:00, 14:00"
            />
          </Box>
        </>
      )}

      {/* Config espera (wait) */}
      {step.type === "wait" && (
        <>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Tempo de espera (segundos)</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 1, max: 300 }}
              value={step.config?.waitSeconds || 5}
              onChange={(e) => onChange("config", { ...step.config, waitSeconds: parseInt(e.target.value) || 5 })}
            />
          </Box>
          <Box className={classes.propSection}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.showCountdown !== false}
                  onChange={(e) => onChange("config", { ...step.config, showCountdown: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography style={{ fontSize: 13 }}>Mostrar contagem regressiva</Typography>}
            />
          </Box>
          <Box className={classes.propSection}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.autoAdvance !== false}
                  onChange={(e) => onChange("config", { ...step.config, autoAdvance: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography style={{ fontSize: 13 }}>Avançar automaticamente ao terminar</Typography>}
            />
          </Box>
        </>
      )}

      {/* Config API externa */}
      {step.type === "api_request" && (
        <>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>URL da API</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.apiUrl || ""}
              onChange={(e) => onChange("config", { ...step.config, apiUrl: e.target.value })}
              placeholder="https://api.exemplo.com/endpoint"
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Método HTTP</Typography>
            <Select
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.method || "GET"}
              onChange={(e) => onChange("config", { ...step.config, method: e.target.value })}
            >
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
            </Select>
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Headers</Typography>
            {(step.config?.headers || []).map((h, hi) => (
              <Box key={hi} display="flex" gap={1} mb={1} alignItems="center">
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Chave"
                  value={h.key || ""}
                  onChange={(e) => {
                    const hdrs = [...(step.config?.headers || [])];
                    hdrs[hi] = { ...hdrs[hi], key: e.target.value };
                    onChange("config", { ...step.config, headers: hdrs });
                  }}
                  style={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Valor"
                  value={h.value || ""}
                  onChange={(e) => {
                    const hdrs = [...(step.config?.headers || [])];
                    hdrs[hi] = { ...hdrs[hi], value: e.target.value };
                    onChange("config", { ...step.config, headers: hdrs });
                  }}
                  style={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => {
                  const hdrs = (step.config?.headers || []).filter((_, i) => i !== hi);
                  onChange("config", { ...step.config, headers: hdrs });
                }}>
                  <DeleteIcon fontSize="small" style={{ color: "#ef4444" }} />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onChange("config", { ...step.config, headers: [...(step.config?.headers || []), { key: "", value: "" }] })}
            >
              Adicionar header
            </Button>
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Corpo da requisição (JSON)</Typography>
            <Typography style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>
              Use {"{{"}<em>variavel</em>{"}"} para inserir respostas coletadas
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              size="small"
              value={step.config?.bodyTemplate || ""}
              onChange={(e) => onChange("config", { ...step.config, bodyTemplate: e.target.value })}
              placeholder={'{"email": "{{lead_email}}", "nome": "{{lead_name}}"}'}
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </Box>
          <Box className={classes.propSection}>
            <Typography className={classes.propLabel}>Mensagem de sucesso</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={step.config?.responseMessage || ""}
              onChange={(e) => onChange("config", { ...step.config, responseMessage: e.target.value })}
              placeholder="Dados enviados com sucesso!"
            />
          </Box>
        </>
      )}

      {/* Lógica condicional — disponível para todas as etapas com resposta */}
      {!NON_ANSWER_TYPES.includes(step.type) && allSteps.length > 1 && (
        <Box className={classes.propSection}>
          <Divider style={{ margin: "8px 0 12px" }} />
          <Typography className={classes.propLabel}>🔀 Lógica condicional</Typography>
          <Typography style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
            Defina o que acontece com base na resposta.
          </Typography>

          {(logic.conditions || []).map((cond, ci) => {
            const isChoice = step.type === "single_choice" || step.type === "yes_no" || step.type === "multiple_choice";
            const updateCond = (patch) => {
              const newConds = [...(logic.conditions || [])];
              newConds[ci] = { ...newConds[ci], ...patch };
              onChange("logic", { ...logic, conditions: newConds });
            };
            return (
              <Box key={ci} mb={1.5} p={1.5} style={{ background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Condição {ci + 1}</Typography>
                  <IconButton size="small" onClick={() => {
                    const newConds = (logic.conditions || []).filter((_, i) => i !== ci);
                    onChange("logic", { ...logic, conditions: newConds });
                  }}>
                    <DeleteIcon fontSize="small" style={{ color: "#ef4444" }} />
                  </IconButton>
                </Box>

                {/* Operador (somente para tipos não-choice) */}
                {!isChoice && (
                  <Box display="flex" gap={1} mb={1}>
                    <Select
                      size="small"
                      variant="outlined"
                      value={cond.operator || "equals"}
                      onChange={(e) => updateCond({ operator: e.target.value })}
                      style={{ flex: 1, fontSize: 13 }}
                    >
                      <MenuItem value="equals">É igual a</MenuItem>
                      <MenuItem value="not_equals">Não é igual a</MenuItem>
                      <MenuItem value="contains">Contém</MenuItem>
                      <MenuItem value="not_contains">Não contém</MenuItem>
                      <MenuItem value="gt">Maior que</MenuItem>
                      <MenuItem value="lt">Menor que</MenuItem>
                      <MenuItem value="not_empty">Não está vazio</MenuItem>
                      <MenuItem value="empty">Está vazio</MenuItem>
                    </Select>
                  </Box>
                )}

                {/* Valor da condição */}
                <Box mb={1}>
                  {isChoice ? (
                    <Select
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={cond.value || ""}
                      onChange={(e) => updateCond({ value: e.target.value })}
                      style={{ fontSize: 13 }}
                    >
                      {(step.type === "yes_no"
                        ? [{ label: "Sim", value: "sim" }, { label: "Não", value: "nao" }]
                        : (step.options || [])
                      ).map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    !["not_empty", "empty"].includes(cond.operator || "equals") && (
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Valor a comparar"
                        value={cond.value || ""}
                        onChange={(e) => updateCond({ value: e.target.value })}
                      />
                    )
                  )}
                </Box>

                {/* Ação */}
                <Box display="flex" gap={1} alignItems="center">
                  <Typography style={{ fontSize: 12, color: "#6b7280", flexShrink: 0 }}>→ Ação:</Typography>
                  <Select
                    size="small"
                    variant="outlined"
                    value={cond.action || "go_to_step"}
                    onChange={(e) => updateCond({ action: e.target.value })}
                    style={{ flex: 1, fontSize: 13 }}
                  >
                    <MenuItem value="go_to_step">Ir para etapa</MenuItem>
                    <MenuItem value="end_quiz">Encerrar formulário</MenuItem>
                    <MenuItem value="redirect_url">Redirecionar para URL</MenuItem>
                  </Select>
                </Box>

                {/* Destino da ação */}
                {(cond.action || "go_to_step") === "go_to_step" && (
                  <Box mt={1}>
                    <Select
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={cond.nextStepId || ""}
                      onChange={(e) => updateCond({ nextStepId: e.target.value })}
                      style={{ fontSize: 13 }}
                    >
                      <MenuItem value=""><em>Selecionar etapa...</em></MenuItem>
                      {allSteps
                        .filter(s => (s._tempId || s.id) !== (step._tempId || step.id))
                        .map((s, si) => (
                          <MenuItem key={s._tempId || s.id} value={s._tempId || s.id}>
                            Etapa {si + 1}: {s.question?.slice(0, 30) || s.type}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                )}

                {cond.action === "redirect_url" && (
                  <Box mt={1}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="https://..."
                      value={cond.redirectUrl || ""}
                      onChange={(e) => updateCond({ redirectUrl: e.target.value })}
                    />
                  </Box>
                )}
              </Box>
            );
          })}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const newConds = [...(logic.conditions || []), { operator: "equals", value: "", action: "go_to_step", nextStepId: "" }];
              onChange("logic", { ...logic, conditions: newConds });
            }}
          >
            Adicionar condição
          </Button>

          {/* Ação padrão (quando nenhuma condição corresponde) */}
          <Box mt={2} pt={1.5} style={{ borderTop: "1px dashed #e5e7eb" }}>
            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Padrão (nenhuma condição atendida):</Typography>
            <Select
              fullWidth
              size="small"
              variant="outlined"
              value={logic.defaultAction || "continue"}
              onChange={(e) => onChange("logic", { ...logic, defaultAction: e.target.value })}
              style={{ fontSize: 13 }}
            >
              <MenuItem value="continue">Continuar para próxima etapa</MenuItem>
              <MenuItem value="go_to_step">Ir para etapa específica</MenuItem>
              <MenuItem value="end_quiz">Encerrar formulário</MenuItem>
              <MenuItem value="redirect_url">Redirecionar para URL</MenuItem>
            </Select>
            {logic.defaultAction === "go_to_step" && (
              <Box mt={1}>
                <Select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={logic.defaultNextStepId || ""}
                  onChange={(e) => onChange("logic", { ...logic, defaultNextStepId: e.target.value })}
                  style={{ fontSize: 13 }}
                >
                  <MenuItem value=""><em>Selecionar etapa...</em></MenuItem>
                  {allSteps
                    .filter(s => (s._tempId || s.id) !== (step._tempId || step.id))
                    .map((s, si) => (
                      <MenuItem key={s._tempId || s.id} value={s._tempId || s.id}>
                        Etapa {si + 1}: {s.question?.slice(0, 30) || s.type}
                      </MenuItem>
                    ))}
                </Select>
              </Box>
            )}
            {logic.defaultAction === "redirect_url" && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="https://..."
                  value={logic.defaultRedirectUrl || ""}
                  onChange={(e) => onChange("logic", { ...logic, defaultRedirectUrl: e.target.value })}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ---------- Aba Aparência ----------
function AppearanceTab({ quiz, onUpdate }) {
  const [allTags, setAllTags] = React.useState([]);

  React.useEffect(() => {
    api.get("/tags").then(r => setAllTags(r.data?.tags || r.data || [])).catch(() => {});
  }, []);

  const normalTags = allTags.filter(t => !t.kanban);
  const kanbanTags = allTags.filter(t => !!t.kanban);

  const captacao = quiz.settings?.captacao || {};
  const notifications = quiz.settings?.notifications || {};

  const updateCaptacao = (patch) => {
    onUpdate({ settings: { ...(quiz.settings || {}), captacao: { ...captacao, ...patch } } });
  };
  const updateNotifications = (section, patch) => {
    onUpdate({
      settings: {
        ...(quiz.settings || {}),
        notifications: {
          ...notifications,
          [section]: { ...(notifications[section] || {}), ...patch },
        },
      },
    });
  };

  const sectionStyle = {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  };
  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 8,
    display: "block",
  };
  const hintStyle = { fontSize: 12, color: "#9ca3af", marginTop: 4 };

  return (
    <Box style={{ padding: 24, maxWidth: 580, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Visual */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
          Visual
        </Typography>
        <Box>
          <Typography style={labelStyle}>Cor primária (botões e destaques)</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <input
              type="color"
              value={quiz.primaryColor || "#6C63FF"}
              onChange={(e) => onUpdate({ primaryColor: e.target.value })}
              style={{ width: 44, height: 44, cursor: "pointer", border: "2px solid #e5e7eb", borderRadius: 10, padding: 2 }}
            />
            <TextField
              size="small"
              variant="outlined"
              value={quiz.primaryColor || "#6C63FF"}
              onChange={(e) => onUpdate({ primaryColor: e.target.value })}
              style={{ width: 130 }}
            />
          </Box>
        </Box>
        <Box>
          <Typography style={labelStyle}>Cor de fundo</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <input
              type="color"
              value={quiz.bgColor || "#ffffff"}
              onChange={(e) => onUpdate({ bgColor: e.target.value })}
              style={{ width: 44, height: 44, cursor: "pointer", border: "2px solid #e5e7eb", borderRadius: 10, padding: 2 }}
            />
            <TextField
              size="small"
              variant="outlined"
              value={quiz.bgColor || "#ffffff"}
              onChange={(e) => onUpdate({ bgColor: e.target.value })}
              style={{ width: 130 }}
            />
          </Box>
        </Box>
        <Box>
          <Typography style={labelStyle}>Texto do botão</Typography>
          <TextField
            size="small"
            variant="outlined"
            value={quiz.buttonText || "Continuar"}
            onChange={(e) => onUpdate({ buttonText: e.target.value })}
            style={{ width: 220 }}
          />
        </Box>
      </Box>

      {/* Conclusão */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
          Conclusão
        </Typography>
        <Box>
          <Typography style={labelStyle}>Mensagem de conclusão</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            value={quiz.completionMessage || ""}
            onChange={(e) => onUpdate({ completionMessage: e.target.value })}
            placeholder="Obrigado! Em breve entraremos em contato."
          />
        </Box>
        <Box>
          <Typography style={labelStyle}>Redirecionar após conclusão <span style={{ fontWeight: 400, color: "#9ca3af" }}>(opcional)</span></Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={quiz.redirectUrl || ""}
            onChange={(e) => onUpdate({ redirectUrl: e.target.value })}
            placeholder="https://..."
          />
        </Box>
      </Box>

      {/* Captação de Lead */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>
          Captação de Lead
        </Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={captacao.createContact !== false}
                onChange={(e) => updateCaptacao({ createContact: e.target.checked })}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 13 }}>Salvar como Contato</Typography>}
          />
        </Box>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={captacao.createLead !== false}
                onChange={(e) => updateCaptacao({ createLead: e.target.checked })}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 13 }}>Salvar como Lead no CRM</Typography>}
          />
        </Box>
        {normalTags.length > 0 && (
          <Box>
            <Typography style={labelStyle}>Tags a adicionar ao contato</Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
              {normalTags.map(t => {
                const selected = (captacao.tagIds || []).includes(t.id);
                return (
                  <Chip
                    key={t.id}
                    label={t.name}
                    size="small"
                    style={{
                      background: selected ? (t.color || "#6C63FF") : "#f3f4f6",
                      color: selected ? "#fff" : "#374151",
                      cursor: "pointer",
                      fontWeight: selected ? 700 : 400,
                      border: `2px solid ${selected ? (t.color || "#6C63FF") : "transparent"}`,
                    }}
                    onClick={() => {
                      const ids = captacao.tagIds || [];
                      updateCaptacao({ tagIds: selected ? ids.filter(i => i !== t.id) : [...ids, t.id] });
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}
        {kanbanTags.length > 0 && (
          <Box>
            <Typography style={labelStyle}>Fase do Kanban</Typography>
            <Select
              fullWidth
              variant="outlined"
              size="small"
              value={captacao.kanbanStageId || ""}
              onChange={(e) => updateCaptacao({ kanbanStageId: e.target.value || null })}
            >
              <MenuItem value=""><em>Nenhuma</em></MenuItem>
              {kanbanTags.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      {/* Notificações */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>
          Notificações ao receber resposta
        </Typography>

        {/* E-mail */}
        <Box style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 16, marginBottom: 16 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!notifications.email?.enabled}
                onChange={(e) => updateNotifications("email", { enabled: e.target.checked })}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 13, fontWeight: 600 }}>Enviar e-mail</Typography>}
          />
          {notifications.email?.enabled && (
            <Box display="flex" flexDirection="column" style={{ gap: 10, marginTop: 10 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Para (e-mail)"
                value={notifications.email?.to || ""}
                onChange={(e) => updateNotifications("email", { to: e.target.value })}
                placeholder="{{lead_email}} ou email@fixo.com"
              />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Assunto"
                value={notifications.email?.subject || ""}
                onChange={(e) => updateNotifications("email", { subject: e.target.value })}
                placeholder="Novo envio: {{quiz_nome}}"
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                label="Corpo do e-mail"
                value={notifications.email?.body || ""}
                onChange={(e) => updateNotifications("email", { body: e.target.value })}
                placeholder={"Nome: {{nome}}\nE-mail: {{email}}\nTelefone: {{telefone}}"}
              />
              <Typography style={hintStyle}>
                Variáveis: {"{{"}<span>nome</span>{"}}"}, {"{{"}<span>email</span>{"}}"}, {"{{"}<span>telefone</span>{"}}"}, {"{{"}<span>quiz_nome</span>{"}}"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* WhatsApp */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={!!notifications.whatsapp?.enabled}
                onChange={(e) => updateNotifications("whatsapp", { enabled: e.target.checked })}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 13, fontWeight: 600 }}>Enviar WhatsApp</Typography>}
          />
          {notifications.whatsapp?.enabled && (
            <Box display="flex" flexDirection="column" style={{ gap: 10, marginTop: 10 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Para (número)"
                value={notifications.whatsapp?.to || ""}
                onChange={(e) => updateNotifications("whatsapp", { to: e.target.value })}
                placeholder="{{lead_phone}} ou 5511999990000"
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                label="Mensagem"
                value={notifications.whatsapp?.message || ""}
                onChange={(e) => updateNotifications("whatsapp", { message: e.target.value })}
                placeholder="Olá {{nome}}, recebemos seu formulário!"
              />
              <Typography style={hintStyle}>
                Usa a primeira conexão WhatsApp ativa da empresa.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Integrações */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
          Integrações
        </Typography>
        <Box>
          <Typography style={labelStyle}>Webhook <span style={{ fontWeight: 400, color: "#9ca3af" }}>(opcional)</span></Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={quiz.actionWebhook || ""}
            onChange={(e) => onUpdate({ actionWebhook: e.target.value })}
            placeholder="https://..."
          />
          <Typography style={hintStyle}>
            Recebe um POST com os dados do lead ao concluir o formulário.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Aba Compartilhar ----------
function ShareTab({ quiz }) {
  if (!quiz) return null;
  const publicUrl = `${window.location.origin}/q/${quiz.slug}`;
  const embedCode = `<iframe src="${publicUrl}" width="100%" height="650" frameborder="0" allow="clipboard-write"></iframe>`;

  const sectionStyle = {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    padding: "20px 24px",
  };

  return (
    <Box style={{ padding: 24, maxWidth: 620, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Banner de status */}
      {quiz.status !== "published" ? (
        <Box style={{ background: "#eff6ff", borderRadius: 10, border: "1px solid #93c5fd", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>💡</span>
          <Typography style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.5 }}>
            Em <strong>rascunho</strong> — acessível para testes. Clique em <strong>Publicar</strong> para distribuição oficial.
          </Typography>
        </Box>
      ) : (
        <Box style={{ background: "#f0fdf4", borderRadius: 10, border: "1px solid #86efac", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <Typography style={{ fontSize: 13, color: "#166534", lineHeight: 1.5 }}>
            <strong>Publicado</strong> — o formulário está disponível publicamente.
          </Typography>
        </Box>
      )}

      {/* Link público */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
          Link público
        </Typography>
        <Typography style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
          Compartilhe este link com seus clientes ou use-o em campanhas.
        </Typography>
        <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={publicUrl}
            InputProps={{
              readOnly: true,
              style: { fontFamily: "monospace", fontSize: 13, background: "#f8fafc" },
            }}
          />
        </Box>
        <Box style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado!"); }}
            style={{ textTransform: "none", borderRadius: 8 }}
          >
            Copiar link
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => window.open(publicUrl, "_blank")}
            style={{ textTransform: "none", borderRadius: 8 }}
          >
            Abrir
          </Button>
        </Box>
      </Box>

      {/* Iframe */}
      <Box style={sectionStyle}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
          Incorporar no site (iframe)
        </Typography>
        <Typography style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
          Cole este código HTML em qualquer página do seu site para exibir o formulário incorporado.
        </Typography>
        <Box style={{ background: "#1e1e2e", borderRadius: 10, padding: "14px 16px", fontFamily: "monospace", fontSize: 12, color: "#a5f3fc", wordBreak: "break-all", lineHeight: 1.7 }}>
          {embedCode}
        </Box>
        <Button
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={() => { navigator.clipboard.writeText(embedCode); toast.success("Código copiado!"); }}
          style={{ marginTop: 12, textTransform: "none", borderRadius: 8 }}
          variant="outlined"
        >
          Copiar código
        </Button>
      </Box>
    </Box>
  );
}

// ---------- BUILDER PRINCIPAL ----------
export default function QuizBuilder() {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();

  const [quiz, setQuiz] = useState(null);
  const [steps, setSteps] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [tab, setTab] = useState(0); // 0=Fluxo, 1=Aparência, 2=Compartilhar
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await showQuiz(id);
      setQuiz(data);
      const sorted = (data.steps || []).sort((a, b) => a.orderIndex - b.orderIndex);
      setSteps(sorted.map(s => ({ ...s, _tempId: String(s.id) })));
    } catch {
      toast.error("Quiz não encontrado");
      history.push("/captacao/quiz");
    }
  }, [id, history]);

  useEffect(() => { load(); }, [load]);

  const saveQuizMeta = useCallback(async (patch) => {
    try {
      await updateQuiz(id, patch);
      setQuiz(q => ({ ...q, ...patch }));
    } catch {
      toast.error("Erro ao salvar configuração");
    }
  }, [id]);

  const handleSaveNow = async () => {
    setSaving(true);
    try {
      await saveSteps(id, steps.map((s, i) => ({ ...s, orderIndex: i })));
      toast.success("Salvo!");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
      setDirty(false);
    }
  };

  const handleTogglePublish = async () => {
    await handleSaveNow();
    const newStatus = quiz.status === "published" ? "draft" : "published";
    await saveQuizMeta({ status: newStatus });
    toast.success(newStatus === "published" ? "Quiz publicado!" : "Voltou para rascunho");
  };

  const addStep = (type) => {
    const step = newStep(type);
    setSteps(prev => {
      const next = [...prev, step];
      setSelectedIdx(next.length - 1);
      return next;
    });
    setDirty(true);
  };

  const updateStep = (idx, field, val) => {
    setSteps(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
    setDirty(true);
  };

  const deleteStep = (idx) => {
    setSteps(prev => {
      const next = prev.filter((_, i) => i !== idx);
      setSelectedIdx(Math.min(idx, next.length - 1));
      return next;
    });
    setDirty(true);
  };

  const moveStep = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    setSteps(prev => {
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      setSelectedIdx(target);
      return next;
    });
    setDirty(true);
  };

  const selectedStep = steps[selectedIdx] || null;

  if (!quiz) return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box className={classes.root}>
      {/* Topbar — 3 seções: esquerda | centro | direita */}
      <Box className={classes.topbar}>
        {/* Esquerda: voltar + nome */}
        <Box className={classes.topbarLeft}>
          <Tooltip title="Voltar para lista">
            <IconButton size="small" onClick={() => history.push("/captacao/quiz")}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={async () => {
                setEditingTitle(false);
                const name = titleDraft.trim();
                if (name && name !== quiz.name) await saveQuizMeta({ name });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") { setEditingTitle(false); }
              }}
              style={{
                border: "none",
                borderBottom: "2px solid #1976d2",
                outline: "none",
                background: "transparent",
                fontWeight: 700,
                fontSize: 15,
                color: "inherit",
                width: 200,
                padding: "2px 0",
                fontFamily: "inherit",
              }}
            />
          ) : (
            <Tooltip title="Clique para renomear">
              <Typography
                className={classes.topbarTitle}
                style={{ cursor: "text" }}
                onClick={() => { setTitleDraft(quiz.name); setEditingTitle(true); }}
              >
                {quiz.name}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Centro: abas de navegação */}
        <Box className={classes.topbarCenter}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            className={classes.topbarTabs}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Fluxo" />
            <Tab label="Aparência" />
            <Tab label="Compartilhar" />
          </Tabs>
        </Box>

        {/* Direita: status + botões */}
        <Box className={classes.topbarRight}>
          {saving && <CircularProgress size={15} />}
          {dirty && !saving && (
            <Typography style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
              Não salvo
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={handleSaveNow}
            disabled={saving}
            style={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Salvar
          </Button>
          <Button
            size="small"
            variant="contained"
            color={quiz.status === "published" ? "default" : "primary"}
            onClick={handleTogglePublish}
            style={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            {quiz.status === "published" ? "Despublicar" : "Publicar"}
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.body}>
        {tab === 0 && (
          <>
            {/* Lista de etapas */}
            <Box className={classes.stepsPanel}>
              <Typography className={classes.stepsHeader}>Etapas</Typography>
              {steps.map((s, i) => {
                const meta = TYPE_META[s.type] || {};
                return (
                  <Box
                    key={s._tempId || s.id}
                    className={`${classes.stepItem} ${i === selectedIdx ? classes.stepItemActive : ""}`}
                    onClick={() => setSelectedIdx(i)}
                  >
                    <Box className={`${classes.stepNum} ${i === selectedIdx ? classes.stepNumActive : ""}`}>{i + 1}</Box>
                    <Box flex={1} minWidth={0}>
                      <Typography className={classes.stepLabel}>{s.question || meta.label || s.type}</Typography>
                      <Typography style={{ fontSize: 10, color: "#6b7280" }}>{meta.label}</Typography>
                    </Box>
                  </Box>
                );
              })}
              {steps.length === 0 && (
                <Typography style={{ color: "#6b7280", fontSize: 13, padding: "12px 14px" }}>
                  Adicione blocos no painel ao lado
                </Typography>
              )}
            </Box>

            {/* Paleta de blocos */}
            <Box className={classes.blocksPanel}>
              {BLOCK_TYPES.map(group => (
                <Box key={group.group} className={classes.blockGroup}>
                  <Typography className={classes.blockGroupLabel}>{group.group}</Typography>
                  {group.items.map(item => (
                    <Tooltip key={item.type} title={item.desc} placement="right">
                      <Box className={classes.blockItem} onClick={() => addStep(item.type)}>
                        <Box className={classes.blockIcon}>{item.icon}</Box>
                        <Typography className={classes.blockLabel}>{item.label}</Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              ))}
            </Box>

            {/* Preview celular */}
            <Box className={classes.previewPanel}>
              <Box className={classes.phone}>
                {/* Barra de progresso */}
                <Box style={{ height: 4, background: "#e5e7eb" }}>
                  <Box style={{
                    height: "100%",
                    background: quiz.primaryColor || "#6C63FF",
                    width: steps.length > 0 ? `${((selectedIdx + 1) / steps.length) * 100}%` : "0%",
                    transition: "width 0.3s",
                  }} />
                </Box>
                <Box className={classes.phoneScreen}>
                  <StepPreview step={selectedStep} quiz={quiz} />
                </Box>
              </Box>
            </Box>

            {/* Painel de propriedades */}
            <PropsPanel
              step={selectedStep}
              onChange={(field, val) => updateStep(selectedIdx, field, val)}
              onDelete={() => deleteStep(selectedIdx)}
              onMoveUp={() => moveStep(selectedIdx, -1)}
              onMoveDown={() => moveStep(selectedIdx, 1)}
              isFirst={selectedIdx === 0}
              isLast={selectedIdx === steps.length - 1}
              allSteps={steps}
            />
          </>
        )}

        {tab === 1 && (
          <Box style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <AppearanceTab quiz={quiz} onUpdate={saveQuizMeta} />
          </Box>
        )}

        {tab === 2 && (
          <Box style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <ShareTab quiz={quiz} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
